import React, { useContext, useEffect, useRef, useState } from 'react';

import { ApplicableTableSettings } from '../../../tableSettings';
import SmartTable, { SmartTableHandle } from '../../../uiControls/SmartTable';
import TableEditContext, { ITableEditContextState } from './tableEditContext';
import TableEditControls, { TableEditControlsHandle } from './TableEditControls';
import ApplicationContext from '../../../ApplicationContext';
import { useTypedStorage } from '../../../hooks/useStorage';
import PrimaryButton from '../../PrimaryButton';
import arrowDownBlackIcon from '../../../img/line-seq-swap-arrow-down-black.svg';
import arrowDownWhiteIcon from '../../../img/line-seq-swap-arrow-down-white.svg';
import arrowDownGrayIcon from '../../../img/line-seq-swap-arrow-down-gray.svg';
import arrowUpBlackIcon from '../../../img/line-seq-swap-arrow-up-black.svg';
import arrowUpWhiteIcon from '../../../img/line-seq-swap-arrow-up-white.svg';
import arrowUpGrayIcon from '../../../img/line-seq-swap-arrow-up-gray.svg';
import { TableCheckBoxRenderer, TableDateRenderer, TableIndicatorRenderer, TableNumberRenderer, TableTextRenderer, TextAlignLeftRenderer } from '../../../uiControls/SmartTableRenderers';
import { TableSearch } from '../TableSearch';
import { useTableState } from '../useMultiTableContext';

export enum TableDateType {
  Date = 'dateonly',
  DateTime = 'datetime',
  DateTimeWhithSec = 'datetimesec'
}

export enum TableCellTypes {
  Text = 'text',
  Checkbox = 'checkbox',
  Number = 'number',
  Date = 'date',
  Null = ''
}

export enum TableCellAligns {
  Left = 'left',
  Center = 'center',
  Right = 'right'
}

export type CellData = {
  type: TableCellTypes;
  value: string | boolean | number;
  decimal?: number;
  align?: TableCellAligns;
  onChangeFunc?: any;
  showZero?: boolean;
  dateType?: TableDateType;
  onLabelClick?: any;
  specialRenderer?: any;
  tooltip?: string;
}

const EditableCellStyle: React.CSSProperties = {
  width: '100%',
};

function GetCellRenderer(value: string | boolean | number, rowIndex: number, accessor: string, type: string, disabled: boolean, align?: string, decimal?: number, onChangeFunc?: any, onLabelClick?: any, dateType?: TableDateType, showZero: boolean = true, tooltip?: string): any {
  if (accessor === 'indicator')
    return <TableIndicatorRenderer rowIndex={rowIndex} onLabelClick={onLabelClick} tooltip={tooltip} />;
  else if (type === 'text') {
    return <TableTextRenderer rowIndex={rowIndex} columnId={accessor} align={align ? align : TableCellAligns.Left} disabled={disabled} value={value} onLabelClick={onLabelClick} />;
  }
  else if (type === 'number') {
    return <TableNumberRenderer rowIndex={rowIndex} columnId={accessor} align={align ? align : TableCellAligns.Right} disabled={disabled} value={value} decimal={decimal} showZero={showZero} />;
  }
  else if (type === 'checkbox') {
    return <TableCheckBoxRenderer rowIndex={rowIndex} columnId={accessor} disabled={disabled} value={value} onChangeFunc={onChangeFunc} />;
  }
  else if (type === 'date') {
    return <TableDateRenderer rowIndex={rowIndex} columnId={accessor} value={value} disabled={disabled} dateType={dateType ? dateType : TableDateType.Date} />;
  }
}

function EditableCellRenderer(props: any) {
  // [241106] MSKim
  // 각 테이블마다 정의된 rowRenderers를 삭제하는 것이 최종 목표
  // Table마다 isCellLock와 getCellData를 정의하고 NonEditCell을 삭제할 수 있음
  const context = useContext(TableEditContext);
  const { row, allColumns, column, data } = props;
  const accessor: string = column.id;
  const columnIndex: number = allColumns.findIndex((x: any) => x.id === accessor);
  const rowRenderer = context.rowRenderers.find(x => x.accessor === accessor);
  const rowIndex: number = row.index;
  const rowData = data[rowIndex];

  const disabled: boolean = context.isCellLock ? context.isCellLock(accessor, rowData, columnIndex, rowIndex) : false;
  const cellData: CellData | undefined = context.getCellData ? context.getCellData(accessor, rowData, rowIndex) : undefined;
  let Renderer = cellData
    ?
    cellData.specialRenderer ? cellData.specialRenderer : GetCellRenderer(cellData.value, rowIndex, accessor, cellData.type, disabled, cellData.align, cellData.decimal, cellData.onChangeFunc, cellData.onLabelClick, cellData.dateType, cellData.showZero, cellData.tooltip)
    :
    rowRenderer.NonEditCell;

  if (accessor === 'indicator') {
    Renderer = <TableIndicatorRenderer rowIndex={rowIndex} onLabelClick={cellData?.onLabelClick} tooltip={cellData?.tooltip}/>
  }

  if (!Renderer) {
    return null;
  }

  if (context.editRowIndex === rowIndex && context.editColumnIndex === columnIndex && context.tempEditColumnIndex !== -1 && rowRenderer.EditCell) { //edit cell
    Renderer = rowRenderer.EditCell;
  }

  return (
    <div
      className={`ifms-editable-cell-${rowIndex}-${columnIndex}`}
      style={EditableCellStyle}
    >
      {(context.editRowIndex === rowIndex && context.editColumnIndex === columnIndex && context.tempEditColumnIndex !== -1 && rowRenderer.EditCell)
        ?
        <Renderer {...props} />
        :
        (cellData && !cellData.specialRenderer) || accessor === 'indicator' ? Renderer : <Renderer {...props} />
      }
    </div>
  );
}

export type EditableTableProps = {
  tableOptions: ApplicableTableSettings,
  deleteLine: (rowIndex: number) => boolean | Promise<boolean>,
  addLine: () => { setDataFunc: (data: any, focusRowIndex: number | undefined) => void, curListData: any, newData: any } | undefined,
  getIndexOfFirstInvalidCell?: (rowIndex: number) => number | undefined,
  getIndexOfFirstInvalidCellAsync?: (rowIndex: number) => Promise<number | undefined>,
  summaryRowCount?: number,
  canDeleteRow?: ((rowIndex: number) => boolean) | undefined,  
  canAdd?: boolean | undefined,
  tableParentDivId?: string,
  showTotalLineNum?: boolean,
  controlsAfterAddLineBtn?: any,
  controlsBeforeAddLineBtn?: any,
  showTopBtn?: boolean,
  showBottomBtn?: boolean,
  lineSeqChange?: ((seqUpDown: string, rowIndex: number) => boolean) | undefined,
  initializeFocusedCell?: boolean,
  tableTitle?:string,
  addRowOnArrowDownInSpecialCase?: ((rowIndex?: number) => number) | undefined,
  shouldConfirmBeforeDelete?: ((rowIndex: number) => boolean) | undefined,
  onInitialized?: VoidFunction,
};

export type EditableTableHandle = {
  resizeColumn: (accessor: string, width: number, persist: boolean) => void,
  showColumn: (accessor: string, visible: boolean, persist: boolean) => void,
  getColumnWidth: (accessor: string) => number,
  add: () => void,
};


const EditableTableImpl: React.ForwardRefRenderFunction<EditableTableHandle, EditableTableProps> = ({
  // function EditableTableImpl({
  tableOptions,
  deleteLine,
  addLine,
  getIndexOfFirstInvalidCell,
  getIndexOfFirstInvalidCellAsync,
  // Number of rows at the bottom that are not actual "data" rows, but provide some additional data.
  // Those rows are excluded when calculating where to put Add line/Edit controls.
  //
  // One example of usage is Profit Share row in crdb,
  summaryRowCount = 0,
  // Number of rows at the top that are not actual "data" rows, but provide some additional data.
  // Those rows are excluded when calculating where to put Add line/Edit controls.
  //
  // One example of usage is Chargeable items table inside AE HAWB Body Info,
  canDeleteRow = undefined,
  canAdd = undefined,
  tableParentDivId = `${tableOptions.customTableId ?? tableOptions.tableName}-editabletable`,
  showTotalLineNum = false,
  controlsAfterAddLineBtn = null,
  controlsBeforeAddLineBtn = null,
  showTopBtn = true,
  showBottomBtn = false,
  lineSeqChange = undefined,
  initializeFocusedCell = undefined,
  tableTitle = '',
  addRowOnArrowDownInSpecialCase = undefined,
  shouldConfirmBeforeDelete = undefined,
  onInitialized = undefined,
}, ref) => {
  const appContext = useContext(ApplicationContext);
  const editControlsRef = useRef<TableEditControlsHandle>(null);

  const [doSelectCell, setDoSelectCell] = useState(false);
  const [lineSeqDownBtnHovered, setLineSeqDownBtnHovered] = useState(false);
  const [lineSeqUpBtnHovered, setLineSeqUpBtnHovered] = useState(false);

  const smartTableRef = useRef<SmartTableHandle>(null);

  tableOptions.tableName = tableOptions.customTableId ?? tableOptions.tableName;
  const tableId = tableOptions.tableName;

  const enableBranch = appContext?.systemSettings?.enableBranch ?? false;
  const useVat = appContext?.systemSettings?.useVat ?? false;

  const accessorsToFilter = [
    ...(!enableBranch ? ['branch', 'br'] : []),
    ...(!useVat ? ['useVat'] : [])
  ];

  const filteredRowRenderers = tableOptions.rowRenderers.filter(
    (x: any) => !accessorsToFilter.includes(x.accessor)
  );

  const { state:tableState, updateState } = useTableState(tableId);

  React.useImperativeHandle(ref, () => ({
    resizeColumn,
    showColumn,
    getColumnWidth,
    add
  }));

  function deleteCellSelected() {
    const selectedCellClass = 'data-cell-selected';
    const currentCellSelections = document.querySelectorAll(`.${selectedCellClass}`);
    currentCellSelections.forEach((cell) => {
      cell.classList.remove(selectedCellClass);
    });
  }

  function add() {
    if (editControlsRef.current) {
      editControlsRef.current.add();
    }
  }

  function resizeColumn(accessor: string, width: number, persist: boolean) {
    if (smartTableRef && smartTableRef.current) {
      smartTableRef.current.resizeColumn(accessor, width, persist);
    }
  }

  function showColumn(accessor: string, visible: boolean, persist: boolean) {
    if (smartTableRef && smartTableRef.current) {
      smartTableRef.current.showColumn(accessor, visible, persist);
    }
  }

  function getColumnWidth(accessor: string): number {
    if (smartTableRef && smartTableRef.current) {
      return smartTableRef.current.getColumnWidth(accessor);
    } else {
      return 0;
    }
  }

  function setTableContaineRef(c: HTMLDivElement) {
    if (editControlsRef.current) {
      editControlsRef.current.setTableReference(c);
    }
  }

  function setEditRowIndex(value: number) {
    updateState({ editRowIndex: value });
  }

  function setEditColumnIndex(value: number) {
    updateState({ editColumnIndex: value });
  }

  function setTempEditColumnIndex(value: number) {
    updateState({ tempEditColumnIndex: value });
    setDoSelectCell(value !== -1);
    if (value !== -1) {
      deleteCellSelected();
    }
  }

  const tableContextState: ITableEditContextState = {
    doSelectCell,
    setDoSelectCell,
    editRowIndex: tableState.editRowIndex,
    editColumnIndex: tableState.editColumnIndex,
    tempEditColumnIndex: tableState.tempEditColumnIndex,
    setEditRowIndex,
    setEditColumnIndex,
    setTempEditColumnIndex,
    deleteLine,
    addLine,
    rowCount: tableOptions.data.length - summaryRowCount,
    data: tableOptions.data,
    rowRenderers: tableOptions.rowRenderers ?? [],
    tableColumnLength: filteredRowRenderers.length,
    isCellLock:tableOptions.isCellLock,
    getCellData:tableOptions.getCellData,
    getIndexOfFirstInvalidCell,
    getIndexOfFirstInvalidCellAsync,
    enabled: tableOptions.useEditControls,
    canDeleteRow,
    canAdd,
    tableParentDivId,
    showTotalLineNum,
    controlsAfterAddLineBtn,
    controlsBeforeAddLineBtn,
    lineSeqChange,
    initializeFocusedCell,
    addRowOnArrowDownInSpecialCase,
    shouldConfirmBeforeDelete,
    useTableSearch: tableOptions.useTableSearch,
  };
  const [, setSelectedIndex] = useTypedStorage<number>(`${tableOptions.tableName}-tableEditRowIndex`, 0);

  useEffect(() => {
    setSelectedIndex(tableState.editRowIndex);
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableState.editRowIndex]);

  const enrichData = (d: any) => ({
    ...d,
    tableName: tableOptions.tableName,
  });

  tableOptions.data = tableOptions.data.map(enrichData);

  tableOptions.rowRenderers.forEach((r: any) => {
    r.Cell = EditableCellRenderer;
  });

  tableOptions.selectable = false;

  
  function LineSeqChangeButton() {
    if (!lineSeqChange) return <></>;
    
    const disableBtn = !tableOptions.useEditControls || tableState.editRowIndex === -1;
    const dataLength = tableOptions.data.length;

    const useDarkMode = appContext.useDarkMode;
    let arrowUpSrc = useDarkMode ? arrowUpWhiteIcon : arrowUpBlackIcon;
    let arrowDownSrc = useDarkMode ? arrowDownWhiteIcon : arrowDownBlackIcon;

    if (disableBtn) {
      arrowUpSrc = arrowUpGrayIcon;
      arrowDownSrc = arrowDownGrayIcon;
    } else if (lineSeqUpBtnHovered) {
      arrowUpSrc = useDarkMode ? arrowUpBlackIcon : arrowUpWhiteIcon;
    } else if (lineSeqDownBtnHovered) {
      arrowDownSrc = useDarkMode ? arrowDownBlackIcon : arrowDownWhiteIcon;
    }

    return (
      <div style={{ display: 'flex' }}>
        <span style={{ marginLeft: '40px', marginTop: '2px' }}>Line Up</span>
        <PrimaryButton
          onMouseEnter={() => setLineSeqUpBtnHovered(true)}
        onMouseLeave={() => setLineSeqUpBtnHovered(false)}
          style={{ marginLeft: '10px' }}
          size="regular"
          disabled={disableBtn}
          onClick={() => {
            if (!disableBtn) {
              const confirm = lineSeqChange('up', tableState.editRowIndex);
              if (confirm && tableState.editRowIndex > 0) {
                setEditRowIndex(tableState.editRowIndex - 1);
              }
            }
          }}
        >
          <div style={{ marginTop: '-4px' }}>
            <img
              className="arrow-down-icon"
              src={arrowUpSrc}
              style={{ marginTop: '1px' }}
              alt=''
            />
          </div>
        </PrimaryButton>
        <span style={{ marginLeft: '15px', marginTop: '2px' }}>Line Down</span>
        <PrimaryButton
          onMouseEnter={() => setLineSeqDownBtnHovered(true)}
          onMouseLeave={() => setLineSeqDownBtnHovered(false)}
          style={{ marginLeft: '10px' }}
          size="regular"
          disabled={disableBtn}
          onClick={() => {
            if (!disableBtn) {
              const confirm = lineSeqChange('down', tableState.editRowIndex);
              if (confirm && tableState.editRowIndex >= 0 && tableState.editRowIndex < dataLength - 1) {
                setEditRowIndex(tableState.editRowIndex + 1);
              }
            }
          }}
        >
          <div style={{ marginTop: '-4px' }}>
            <img
              className="arrow-down-icon"
              src={arrowDownSrc}
              style={{ marginTop: '1px' }}
              alt=''
            />
          </div>
        </PrimaryButton>
      </div>
    )
  }

  // function AddLineButton(location: string) {
  //   if (hideDefaultAddLine) return;
  //   const addLineLabel = useAltLShortcutForAddLine
  //     ? <span>Add <strong><u>L</u></strong>ine</span>
  //     : 'Add Line';
  //   const buttonStyle = useAltLShortcutForAddLine || addLineLabel === 'Add Line' ? { paddingLeft: '20px', paddingRight: '20px' } : {};

  //   const disableAddBtn = !tableOptions.useEditControls;
  //   if (location === 'Top') {
  //     return (
  //       <PrimaryButton
  //         style={buttonStyle}
  //         focusable={true}
  //         name = {customAddLineName}
  //         id="table-top-addline-button"
  //         skipTableCheck
  //         size="regular"
  //         onClick={add}
  //         disabled={disableAddBtn}
  //       >
  //         {customAddTitle || addLineLabel}
  //       </PrimaryButton>
  //     );
  //   }
  //   else {
  //     return (
  //       // <span
  //       //   style={
  //       //     {
  //       //       marginBottom: '5px',
  //       //       color: disableAddBtn ? 'gray' : 'black',
  //       //       fontWeight: 'bold',
  //       //       textDecoration: 'underline',
  //       //       cursor: disableAddBtn ? 'default' : 'pointer',
  //       //       fontSize: '11px'
  //       //     }}
  //       //   onClick={() => {
  //       //     if (!disableAddBtn) add();
  //       //   }}
  //       // >
  //       //   {customAddTitle || 'Add Line'}
  //       // </span>
  //       <PrimaryButton
  //         style={buttonStyle}
  //         focusable={true}
  //         name="tableAddline"
  //         id="table-bottom-addline-button"
  //         skipTableCheck
  //         size="regular"
  //         onClick={add}
  //         disabled={disableAddBtn}
  //       >
  //         {customAddTitle || addLineLabel}
  //       </PrimaryButton>
  //     );
  //   }
  // }

  function totalLineControl() {
    if (showTotalLineNum)
      return (
        <>
        {tableTitle &&
            <span
              style={{
                marginTop: '3px',
                marginRight: '15px',
                fontWeight: 'bold',
              }}>
              {tableTitle}
            </span>
          }
          <span
            style={{
              marginTop: '3px',
              marginRight: '10px',
              fontWeight: 'bold',
            }}>
            {/* Total Line Items: {tableOptions.data.length - summaryRowCount} */}
            Total Line Items: {tableOptions.data.length}
          </span>
        </>
      )
    else
      return null;
  }

  function renderTopControls() {
    return (
      //250422 hykim - Tooltip Render 부분으로 포지션이 변경되는데 margin때문에 불필요하게 화면이 밀리는 문제가 있어 제거
      // <div style={{ marginLeft: '25px', marginBottom: '5px', display: 'flex', position: tableOptions.headerRow ? 'absolute' : 'static' }}>
      <div style={{ display: 'flex', position: tableOptions.headerRow ? 'absolute' : 'static' }}>
        {!showTotalLineNum && (<span style={{ marginTop: '3px', marginRight: '15px', fontWeight: 'bold' }}>{tableTitle}</span>)}
        {totalLineControl()}
        {!!controlsBeforeAddLineBtn && controlsBeforeAddLineBtn()}
        {/* {AddLineButton('Top')} */}
        {!!controlsAfterAddLineBtn && controlsAfterAddLineBtn()}
      </div>
    )
  }

  function renderBottomControls() {
    return (
      <div style={{
        marginLeft: '25px',
        //marginBottom: '5px', 
        position: 'relative',
        top: '4px',
        display: 'flex',
      }}>
        {/* {AddLineButton('Bottom')} */}
        {!!lineSeqChange && LineSeqChangeButton()}
      </div>
    )
  }

  return (
    <div id={`${tableOptions.tableName}-editabletable`} style={{position: 'relative'}}>
      {tableOptions.useTableSearch && <TableSearch  hidden={true}/>}
      {showTopBtn ? renderTopControls() : null}
      <div className={`${tableOptions.tableName}-hideforfocus-top ifms-tabable-active`} style={{ display: 'none' }}></div>
      <TableEditContext.Provider value={tableContextState}>
        <TableEditControls
          tableOptions={tableOptions}
          ref={editControlsRef} />
        <div ref={setTableContaineRef}>
          <SmartTable
            cellRangeSelect={true}
            renderBottomControls={showBottomBtn ? renderBottomControls : null}
            selectedRowIndex={tableState.editRowIndex}
            selectedColIndex={tableState.editColumnIndex}
            ref={smartTableRef}
            onInitialized = {onInitialized}
            {...tableOptions}
          />
        </div>
      </TableEditContext.Provider>
      <div className={`${tableOptions.tableName}-hideforfocus-bottom ifms-tabable-active`} style={{ display: 'none' }}></div>
    </div>
  );
};

const EditableTable = React.forwardRef(EditableTableImpl);

export default EditableTable;
